"use client";

import ProductPage from "@/components/ProductPage";
import { KassensystemHardwareSection } from "@/components/KassensystemHardwareSection";

import ImageHero from "./hero.jpg";
import ImageHighlight from "./Boutique.png";
import ImageFeatureEC from "./feature-ec.jpg";
import ImageFeatureCustomers from "./feature-customers.jpg";
import ImageFeatureStock from "./feature-stock.jpg";
import ImageFeatureStats from "./feature-stats.jpg";
import ImageFeatureOrder from "./feature-order.jpg";
import ImageFeatureExport from "./feature-export.jpg";
import Section from "@/components/Section";
import ImageText from "@/components/ImageText";
import { Check } from "lucide-react";
import SpecialFeaturesSection from "@/components/SpecialFeaturesSection";
import { PricingCard } from "@/components/PricingSection";
import Image from "next/image";

export default function Page() {
  return (
    <ProductPage
      productName="Cashtex Profi – Kassensystem für den Textileinzelhandel"
      hero={{
        title: "Cashtex Profi – Kassensystem für den Textileinzelhandel",
        subtitle:
          "Effiziente Verkaufsprozesse, intuitive Artikel- und Kundenverwaltung sowie klare Umsatzanalysen – optimiert für Boutiquen, Modehäuser und Filialen.",
        buttonLeft: {
          title: "Unverbindliche Anfrage",
          href: "/support",
        },
        buttonRight: { title: "Preise", href: "/preise" },
        image: ImageHero,
        imageAlt: "Cashtex Profi Kassensystem",
      }}
      highlight={{
        image: ImageHighlight,
        bullets: [
          <>
            <strong>Schneller Verkauf</strong> inkl. Retouren, Gutschriften,
            Lieferscheinen und Belegdruck.
          </>,
          <>
            <strong>Textil-Features</strong> für Größen/Farben,
            Kollektionsbilder, Etiketten und Scanner-Inventur.
          </>,
          <>
            <strong>Auswertungen in Echtzeit</strong> (Renner/Penner,
            Mitarbeitende, Lieferanten) für klare Entscheidungen.
          </>,
          <>
            <strong>
              <a
                href="https://www.hsp-steuer.de/2017/01/18/gobs-gobd-gdpdu-einfach-erklaert/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-brand-primary underline hover:opacity-80"
              >
                GoBD
              </a>
              /
              <a
                href="https://www.hsp-steuer.de/2017/01/18/gobs-gobd-gdpdu-einfach-erklaert/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-brand-primary underline hover:opacity-80"
              >
                GDPdU
              </a>
              -konform
            </strong>{" "}
            inkl. Kassenbuch und{" "}
            <a
              href="https://www.softguide.de/funktion/datev-export"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-primary underline hover:opacity-80"
            >
              DATEV
            </a>
            /
            <a
              href="https://www.softguide.de/funktion/fibu-export"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-primary underline hover:opacity-80"
            >
              FIBU-Export
            </a>
            /
            <a
              href="https://www.seeburger.com/de/ressourcen/wissenswertes/was-ist-edi"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-primary underline hover:opacity-80"
            >
              EDI-Schnittstelle
            </a>
            /
            <a
              href="https://www.bsi.bund.de/DE/Themen/Unternehmen-und-Organisationen/Standards-und-Zertifizierung/Schutz-vor-Manipulation-an-digitalen-Grundaufzeichnungen/Fragen-und-Antworten/fragen-und-antworten_node.html"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-primary underline hover:opacity-80"
            >
              TSE
            </a>
            -konform/Schnittstelle zu Ihrem Steuerberater.
          </>,
          <>
            <strong>Finanzamt- und Gesetzkonform</strong>
          </>,
        ],
      }}
      beforeVideo={
        <Section variant="transparent" className="py-4 md:py-6 mt-12">
          <ImageText
            imagePosition="right"
            imageSlot={
              <div className="relative overflow-hidden mt-15 rounded-2xl border border-black/10 bg-black shadow-sm h-100">
                <video className="h-full w-full object-cover" controls>
                  <source src="/VideoFürWebseite.mp4" type="video/mp4" />
                </video>
              </div>
            }
          >
            <div className="flex flex-col gap-2 mb-2">
              <Image
                src="/CTXr.svg"
                alt="Cashtex"
                width={120}
                height={36}
                className="object-contain"
              />
              <h2 className="text-3xl md:text-4xl font-semibold text-[#050a18]">
                Kassensystem für den Textileinzelhandel
              </h2>
            </div>

            <p className="text-slate-700 leading-relaxed">
              Cashtex wurde speziell für den Textileinzelhandel entwickelt und
              unterstützt Sie bei allen täglichen Prozessen – vom Verkauf bis
              zur Auswertung.
            </p>

            <p className="mt-2 text-slate-700 leading-relaxed">
              Durch die intuitive Bedienung und die branchenspezifischen
              Funktionen behalten Sie jederzeit den Überblick über Artikel,
              Kunden und Umsätze.
            </p>

            <ul className="mt-2 space-y-2">
              <li className="flex items-start gap-3 text-slate-700">
                <Check className="mt-1 text-brand-primary" size={18} />
                <span>
                  <strong>Schnelle Abläufe:</strong> Verkauf, Retouren und
                  Belege direkt an der Kasse.
                </span>
              </li>

              <li className="flex items-start gap-3 text-slate-700">
                <Check className="mt-1 text-brand-primary" size={18} />
                <span>
                  <strong>Volle Kontrolle:</strong> Lager, Kunden und Umsätze
                  immer im Blick.
                </span>
              </li>

              <li className="flex items-start gap-3 text-slate-700">
                <Check className="mt-1 text-brand-primary" size={18} />
                <span>
                  <strong>Branchenspezifisch:</strong> Perfekt für Mode- und
                  Textileinzelhandel.
                </span>
              </li>
            </ul>
          </ImageText>

          <SpecialFeaturesSection
            industries={[
              {
                title: "Einzelhandel Textil",
                standard: [
                  "Kassieren",
                  "Artikelverwaltung",
                  "Zahlungsarten",
                  "Tagesabschluss",
                  "Belegausgabe",
                  "Rabatte & Aktionspreise",
                  "Auswertungen",
                  "Mehrwertsteuer-Verwaltung",
                ],
                special: [
                  "Schneller und intuitiver Kassiervorgang im Alltag",
                  "Größen- und Farbmatrix für Textilartikel",
                  "Saisonverwaltung für Kollektionen",
                  "Flexible Zahlungsarten und Belegausgabe",
                  "Klare Auswertungen für bessere Entscheidungen",
                  "Kundenverwaltung mit Kaufhistorie",
                  "Übersichtliche Artikel- und Lagerverwaltung",
                  "Automatische Lagerbestandsüberwachung mit Mindestmengen",
                ],
              },
            ]}
          />
        </Section>
      }
      faqs={[
        {
          q: "Unterstützt Cashtex meine Hardware (Scanner, Drucker)?",
          a: "Ja. Gängige Barcodescanner, Bondrucker und Etikettendrucker werden unterstützt. Welche Geräte konkret kompatibel sind, klären wir gemeinsam im Onboarding.",
        },
        {
          q: "Ist Cashtex für kleine Boutiquen bis Filialbetriebe geeignet?",
          a: "Ja. Die Lösung ist modular aufgebaut und skaliert problemlos von der Einzelkasse bis zu mehreren Filialen – Sie starten klein und wachsen mit dem System.",
        },
        {
          q: "Wie erfolgt der Export zum Steuerberater?",
          a: "Per DATEV-, Lexware- oder FIBU-Export sowie GoBD-konformer Datenbereitstellung – für eine reibungslose Übergabe ohne manuelle Nacharbeit.",
        },
        {
          q: "Hilft Cashtex bei der Inventur?",
          a: "Ja. Inventurlisten, Zählscheine und Scanner-Workflows vereinfachen die Bestandsaufnahme erheblich – schnell, übersichtlich und fehlerfrei.",
        },
        {
          q: "Wie starte ich mit der Demoversion?",
          a: "Einfach unverbindlich anfragen – wir melden uns zeitnah mit einem Terminvorschlag und richten die Demo individuell für Sie ein.",
        },
        {
          q: "Unterstützt Cashtex die Größen- und Farbmatrix für Textilartikel?",
          a: "Ja, Cashtex ist speziell für den Textilbereich ausgelegt und unterstützt die Verwaltung von Artikeln nach Größe, Farbe und Saison.",
        },
        {
          q: "Kann ich Kundenkarten oder Bonusprogramme abbilden?",
          a: "Ja, Cashtex unterstützt Kundenverwaltung mit Kaufhistorie sowie Rabatt- und Bonusprogramme – direkt an der Kasse abrufbar.",
        },
        {
          q: "Wie läuft die Einrichtung und Schulung ab?",
          a: "Nach einer persönlichen Beratung richtet unser Team das System für Sie ein. Schulungen und Support sind im Einführungsprozess inbegriffen.",
        },
      ]}
      features={[
        {
          src: ImageFeatureEC,
          alt: "Cashtex – schneller Verkauf und Kassenprozess",
          title: "Schneller Verkauf & Kassenprozess",
          desc: "Quittungen, Retouren, Gutschriften, Lieferscheine und Belegdruck – reibungslos direkt an der Kasse.",
        },
        {
          src: ImageFeatureCustomers,
          alt: "Cashtex – Kundenverwaltung für Boutiquen",
          title: "Kundenverwaltung",
          desc: "Bonusprogramme, Geburtstagsmodul, Serien-E-Mail/-Brief, Kundenkarten und Umsatzanalysen – alles zentral.",
        },
        {
          src: ImageFeatureStock,
          alt: "Cashtex – Artikel- und Lagerverwaltung im Textilhandel",
          title: "Artikel- & Lagerverwaltung",
          desc: "Artikelstämme, Größen/Farben, Kollektionsbilder, Mehrpreisstufen, Barcode- & Etikettendruck, Inventur via Scanner.",
        },
        {
          src: ImageFeatureStats,
          alt: "Cashtex – Verkaufsstatistiken und Auswertungen",
          title: "Verkaufsstatistiken",
          desc: "Renner-/Penner-Listen, Auswertungen nach Mitarbeitenden, Artikeln & Lieferanten – klare Entscheidungen treffen.",
        },
        {
          src: ImageFeatureOrder,
          alt: "Cashtex – Bestellwesen und Wareneingang",
          title: "Bestellwesen & Wareneingang",
          desc: "Lieferantenbestellungen, Orderübersicht, Wareneingang per Scanner, Kontrolle offener Bestellungen.",
        },
        {
          src: ImageFeatureExport,
          alt: "Cashtex – Kassenbuch und Exporte",
          title: "Kassenbuch & Export",
          desc: "Einnahmen/Ausgaben, revisionssicheres Kassenbuch, Exporte für DATEV/Lexware/FIBU – weniger Buchhaltungsaufwand.",
        },
      ]}
    >
      {/* PRICING CARDS */}
      <Section className="pb-24 bg-white border-y border-gray-50">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-7xl mx-auto px-4">
          <PricingCard
            title="Leasing ab"
            priceFull="24"
            priceCents="90"
            details="Basierend auf 0,83 € / Tag."
            details2="36 / 48 / 60 Monate Laufzeit."
            features={[
              "Warenwirtschaft ModeProfi / CashTex",
              "Voller Support & Updates & Schulungen",
              "Inklusive Kassenbuch",
              "Angebote, Order & Rechnungen",
              "1 Benutzer",
            ]}
          />

          <PricingCard
            title="Mieten ab"
            priceFull="48"
            priceCents="90"
            details="Basierend auf 1,63 € / Tag."
            details2="36 / 48 / 60 Monate Laufzeit."
            features={[
              "Warenwirtschaft ModeProfi / CashTex",
              "Voller Support & Updates & Schulungen",
              "Inklusive Kassenbuch",
              "Ideal für wachsende Unternehmen",
              "1 Benutzer",
            ]}
          />

          <PricingCard
            title="Ratenkauf ab"
            priceFull="74"
            priceCents="90"
            details="Basierend auf 2,49 € / Tag."
            details2="36 / 48 / 60 Monate Laufzeit. Restwert 10 %"
            features={[
              "Warenwirtschaft ModeProfi / CashTex",
              "Voller Support & Updates & Schulungen",
              "Inklusive Kassenbuch",
              "Eigentumserwerb nach Laufzeit",
              "1 Benutzer",
            ]}
          />
        </div>

        <div className="text-center mt-12 text-sm font-bold text-gray-900 tracking-widest uppercase">
          Alle Preise exkl. MwSt.
        </div>
      </Section>
      <KassensystemHardwareSection />
      <Section variant="gray1" className="tse-section">
        <ImageText
          imagePosition="left"
          imageSrc="/tse.png"
          imageAlt="Softwareentwicklung"
          title="TSE & Rechtssicherheit: Wir machen es einfach!"
        >
          <p>
            TSE-Pflicht oder neue Anforderungen vom Finanzamt? Kein Problem –
            wir kennen uns aus und übernehmen die Umsetzung der Technischen
            Sicherheitseinrichtung für Dich und informieren Dich über alle
            wichtigen Änderungen.
          </p>
          <ul className="ps-8">
            <li>
              <Check className="inline -ms-8 text-brand-primary" />{" "}
              <strong>Kein zusätzlicher Aufwand:</strong> Regelmäßige und
              kostenlose Updates
            </li>
            <li>
              <Check className="inline -ms-8 text-brand-primary " />{" "}
              <strong>Rechtssicher:</strong> TSE-konform – erfüllt KassenSichV
              und GoBD
            </li>
            <li>
              <Check className="inline -ms-8 text-brand-primary" />{" "}
              <strong>Zuverlässig:</strong> Datensicherheit auf deutschen
              Servern und nach höchsten Standards
            </li>
          </ul>
          <p>
            Mit der Kassensoftware von SDI arbeitest Du immer 100%
            finanzamtkonform und bist automatisch auf der sicheren Seite.
          </p>
        </ImageText>
      </Section>
    </ProductPage>
  );
}
